import { z } from "zod";
import httpClient from "@/config/axios";

const overviewSchema = z.object({
  group_id: z.string(),
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  teams: z.array(z.object({ team_id: z.number(), team_name: z.string() })),
  nurses: z.array(
    z.object({
      nurse_id: z.string(),
      name: z.string(),
      role: z.string().nullable().optional(),
      team_id: z.number().nullable(),
      team_name: z.string().nullable(),
      is_submitted: z.boolean(),
      shifts: z.array(z.object({ shift_date: z.string(), shift: z.string() })),
    })
  ),
  my_groups: z.array(
    z.object({
      group_id: z.string(),
      group_name: z.string().nullable(),
      kind: z.enum(["home", "inbound"]),
    })
  ),
});
export type WantedOverview = z.infer<typeof overviewSchema>;
export type WantedOverviewAccount = {
  readonly account_id: string;
  readonly office_id: string;
};

export const wantedOverviewKeys = {
  month: (user: WantedOverviewAccount | null, year: number, month: number) =>
    [
      "wanted-overview",
      user?.account_id ?? "",
      user?.office_id ?? "",
      year,
      month,
    ] as const,
  query: (
    user: WantedOverviewAccount | null,
    period: { readonly year: number; readonly month: number },
    groupId: string
  ) =>
    [
      ...wantedOverviewKeys.month(user, period.year, period.month),
      groupId,
    ] as const,
};

export async function fetchWantedOverview({
  year,
  month,
  groupId,
  signal,
}: {
  readonly year: number;
  readonly month: number;
  readonly groupId: string;
  readonly signal?: AbortSignal;
}): Promise<WantedOverview> {
  // Always explicit: axios otherwise injects the global selected (authoring) ward.
  const data = overviewSchema.parse(
    await httpClient.get<unknown>(`/wanted/${year}/${month}/overview`, {
      params: { group_id: groupId },
      signal,
    })
  );
  if (data.group_id !== groupId || data.year !== year || data.month !== month) {
    throw new Error(
      "요청한 병동·기간의 원티드 현황과 응답이 일치하지 않습니다."
    );
  }
  return data;
}
